'use client';

import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Only log full stack in development to avoid leaking internals in production
    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorBoundary]', error, info.componentStack);
    } else {
      console.error('[ErrorBoundary] Unhandled error:', error?.message ?? 'Unknown error');
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex flex-col items-center justify-center min-h-[200px] p-8 text-center" role="alert" aria-live="assertive">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4" aria-hidden="true">
            <AlertTriangle size={20} className="text-red-500" />
          </div>
          <h3 className="text-sm font-semibold text-foreground mb-1">Si è verificato un errore</h3>
          <p className="text-xs text-muted-foreground mb-4 max-w-sm">
            {this.state.error?.message ?? 'Errore imprevisto nel componente. Ricarica la pagina per riprovare.'}
          </p>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            aria-label="Ricarica la pagina per riprovare"
            className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <RefreshCw size={13} aria-hidden="true" />
            Ricarica pagina
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
